import { PHASE_PRODUCTION_BUILD } from 'next/dist/shared/lib/constants';

/**
 * Enum of possible environments.
 *
 * Avoid typos by using an enum.
 */

export enum ENVIRONMENT {
  Production = 'production',
  Staging = 'staging',
  Development = 'development',
  Local = 'local',
}

/**
 * Current Node.js environment.
 */

export const NODE_ENV = process.env.NEXT_PUBLIC_NODE_ENV || ENVIRONMENT.Local;

/**
 * Boolean flag to determine if we are in production.
 */

export const IS_PRODUCTION = NODE_ENV === ENVIRONMENT.Production;

/**
 * Boolean flag to determine if we are in staging.
 */

export const IS_STAGING = NODE_ENV === ENVIRONMENT.Staging;

/**
 * Boolean flag to determine if we are in development.
 *
 * Note: development is not local.
 */

export const IS_DEVELOPMENT = NODE_ENV === ENVIRONMENT.Development;

/**
 * Boolean flag to determine if we are in local environment.
 */

export const IS_LOCAL = NODE_ENV === ENVIRONMENT.Local;

/**
 * Scheme to use for URLs.
 */

export const SCHEME = IS_LOCAL ? 'http' : 'https';

/**
 * Current Next.js phase.
 *
 * Useful to determine if we are building the app.
 */

export const NEXT_PHASE = process.env.NEXT_PHASE || '';

/**
 * Next.js revalidation duration.
 *
 * Set to 10 seconds in local environment to have fresh data while not breaking Next.js with 0.
 */

export const NEXT_REVALIDATE_DURATION = IS_LOCAL ? 10 : 60 * 60 * 4; // 4 hours.

/**
 * Next.js default image quality.
 */

export const NEXT_IMAGE_QUALITY = 75;

/**
 * Boolean flag to determine if Next.js is building the app.
 */

export const IS_BUILDING = NEXT_PHASE === PHASE_PRODUCTION_BUILD;

/**
 * API token to access the CMS.
 */

export const API_TOKEN = process.env.API_TOKEN || '';

/**
 * Drupal's CMS base domain.
 */

export const API_DOMAIN = process.env.NEXT_PUBLIC_API_DOMAIN || 'garda-c24-cms.ddev.site';

/**
 * Current website slug for the API.
 */

export const API_WEBSITE = process.env.NEXT_PUBLIC_API_WEBSITE as 'crisis24' | 'codered' | 'aiia';

/**
 * Current website base URL.
 */

export const DOMAIN = process.env.NEXT_PUBLIC_DOMAIN || 'localhost:3000';

/**
 * GardaWorld base domain.
 *
 * Used for footer links
 */

export const GARDAWORLD_DOMAIN = process.env.NEXT_PUBLIC_GARDAWORLD_DOMAIN || 'localhost:3001';

/**
 * Google Tag Manager key.
 */

export const GTM_KEY = process.env.NEXT_PUBLIC_GTM_KEY || '';

/**
 * Google Recaptcha keys.
 */

export const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_KEY || '';

/**
 * Google Recaptcha secret.
 */

export const RECAPTCHA_SECRET = process.env.RECAPTCHA_SECRET || '';

/**
 * Google Maps API key.
 */

export const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

/**
 * Algolia application ID.
 */

export const ALGOLIA_APPLICATION_ID = process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID || '';

/**
 * Algolia admin API key.
 */

export const ALGOLIA_ADMIN_API_KEY = process.env.ALGOLIA_ADMIN_API_KEY || '';

/**
 * Algolia search-only API key.
 */

export const ALGOLIA_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || '';

/**
 * Algolia index name.
 */

export const ALGOLIA_INDEX = process.env.NEXT_PUBLIC_ALGOLIA_INDEX || '';

/**
 * Algolia index replica for date ascending sorting.
 */

export const ALGOLIA_INDEX_REPLICA_DESC = `${ALGOLIA_INDEX}_date_desc`;

/**
 * Cookie Pro key for the current website.
 */

export const COOKIE_PRO_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_PRO_DOMAIN || '';

/**
 * Default dimension for SVG images coming from Drupal.
 */

export const DRUPAL_SVG_DIMENSION = 64;
